import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import { get, map, join, includes, size, filter } from "lodash";

import Grid from "@material-ui/core/Grid";
import Tooltip from "@material-ui/core/Tooltip";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";

import TagColor from "../../Common/TagColor";
import Picture from "../../Common/Picture";
import EllipsedTextNew from "../../Common/EllipsedTextNew";

import {
  getAttributeValue,
  getAttributeValues,
  getProfileImage,
  getProfileTypeIcon,
  isUnknownProfile,
} from "../../../utils/profile";
import { formatPhone } from "../../../utils/form";

import colors from "../../../assets/style/colors";
import images from "../../../assets/img/images";
import ProfileMatchExpressUnknown from "./ProfileMatchExpressUnknown";
import { getLov } from "../../../utils/lov";

const styles = {
  viewLink: {
    color: colors.primary,
    cursor: "pointer",
    fontSize: "0.86em",
  },
  checkmarkContainer: {
    color: colors.black54,
    minWidth: "30px",
    textAlign: "center",
    fontSize: "1.7em",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pictureContainer: {
    width: "73px",
    minWidth: "73px",
    height: "73px",
    border: `1px solid ${colors.veryLighPink}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  infoContainer: {
    padding: "5px 4px",
    lineHeight: "16px",
    "& img": {
      maxHeight: "12px",
    },
  },
  ocrPlus: {
    color: colors.primary,
    marginLeft: "5px",
    cursor: "pointer",
    "& :hover": {
      textDecoration: "underline",
    },
  },
  tooltip: {
    marginTop: "0px",
    fontSize: "1em",
    fontWeight: "400",
  },
};

const ProfileMatchExpress = (props) => {
  const { classes, language, profile, associations, handleClose = false, allow = true } = props;
  const [lov, setLov] = React.useState();
  const [openTooltip, setOpenTooltip] = React.useState(false);

  const handleTooltipClose = (e) => {
    e.stopPropagation();
    setOpenTooltip(false);
  };

  const handleTooltipOpen = (e) => {
    e.stopPropagation();
    setOpenTooltip(!openTooltip);
  };

  React.useEffect(() => {
    const fetchLov = async () => {
      try {
        setLov({
          89: await getLov(89),
        });
      } catch (e) {
        console.log(e);
      }
    };
    fetchLov();
  }, []);

  const profileImage = getProfileImage(profile, props.allowAnyImage);

  if (isUnknownProfile(get(profile, ["id"]))) {
    return (
      <ProfileMatchExpressUnknown
        type={get(profile, ["type", "id"])}
        topRightNode={props.topRightNode}
        style={props.style}
      />
    );
  }

  if (!handleClose) {
    return (
      <Grid
        item
        xs
        style={{
          padding: "10px",
          borderRadius: "2px",
          backgroundColor: colors.lightGrey,
          ...props.style,
        }}
        className={props.className}
        onClick={props.onClick}
      >
        <Grid container wrap="nowrap">
          <Grid
            item
            className={classes.pictureContainer}
            style={{ backgroundColor: !profileImage ? colors.mediumGrey : "" }}
          >
            {profileImage ? (
              <Picture
                url={profileImage}
                alt="profile_picture"
                maxHeight={73}
                style={{ maxWidth: "73px", maxHeight: "73px" }}
              />
            ) : (
              <img src={images.humanIconBlack} width="50px" alt="human" />
            )}
          </Grid>
          <Grid item xs className={classes.infoContainer} style={{ width: 220 }}>
            <Grid container direction="column">
              <Grid item>
                <Grid container>
                  <Grid item style={{ paddingRight: "5px" }}>
                    <img
                      src={getProfileTypeIcon(get(profile, ["type", "id"]))}
                      height={"12px"}
                      alt="profile_icon"
                    />
                  </Grid>
                  <Grid item xs className="bold-500 text-87">
                    {get(profile, ["type", "id"]) === 1 &&
                      get(profile, ["firstname"])
                      ? `${get(profile, ["firstname"])} ${get(profile, [
                        "lastname",
                      ])}`
                      : get(profile, ["name"]) || I18n.get("site_unknown")}
                  </Grid>
                  {props.topRightNode && <Grid item>{props.topRightNode}</Grid>}
                </Grid>
              </Grid>
              <Grid
                item
                style={{
                  maxHeight: "20px",
                  overflow: "hidden",
                }}
              >
                <Grid container wrap="nowrap">
                  {associations && (
                    <Grid item xs={6}>
                      <Grid container wrap="nowrap">
                        <Grid item style={{ paddingRight: "5px" }}>
                          <img src={images.relationIcon} alt="relation" />
                        </Grid>
                        <Grid
                          item
                          className="text-54"
                          style={{ paddingRight: "9px", maxHeight: "20px" }}
                        >
                          <EllipsedTextNew
                            text={join(
                              map(associations, (a) =>
                                get(a, [`name_${language}`])
                              ),
                              ", "
                            )}
                            maxWidth={95}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <TagColor
                      color={
                        get(profile, ["tag", "color", "code"])
                          ? `#${profile.tag.color.code}`
                          : get(profile, ["tag", "color_rgb"])
                            ? `#${profile.tag.color_rgb}`
                            : "undefined"
                      }
                      name={
                        get(profile, ["tag", `name_${language}`]) ||
                        I18n.get("site_unknown")
                      }
                      maxWidth={100}
                      style={{ maxHeight: "16px" }}
                    />
                  </Grid>
                  {getAttributeValue(profile, 89) && (
                    <Grid item xs={6}>
                      <Grid container wrap="nowrap">
                        <Grid item style={{ paddingRight: "5px" }}>
                          <img src={images.carrierIcon} alt="carrier" />
                        </Grid>
                        <Grid
                          item
                          className="text-54"
                          style={{ paddingRight: "9px", maxHeight: "20px" }}
                        >
                          <EllipsedTextNew
                            text={getAttributeValue(profile, 89, lov, language)}
                            maxWidth={95}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              {get(profile, ["type", "id"]) === 1 && (
                <>
                  {getAttributeValue(profile, 34) && (
                    <Grid item className="flexCenter">
                      <img src={images.phoneIcon} alt="phone" />
                      <span
                        className="text-54"
                        style={{ overflowWrap: "anywhere", paddingLeft: "5px" }}
                      >
                        {formatPhone(getAttributeValue(profile, 34))}
                      </span>
                    </Grid>
                  )}
                  {getAttributeValue(profile, 33) && (
                    <Grid item className="flexCenter">
                      <img src={images.emailIcon} alt="email" />
                      <span
                        className="text-54"
                        style={{ maxHeight: "16px", paddingLeft: "5px" }}
                      >
                        <EllipsedTextNew
                          text={getAttributeValue(profile, 33)}
                          maxWidth={228}
                        />
                      </span>
                    </Grid>
                  )}
                </>
              )}
              {includes([2, 3], get(profile, ["type", "id"])) && (
                <>
                  {getAttributeValue(profile, 44) && (
                    <Grid item className="flexCenter">
                      <span className="bold-500">{I18n.get("site_lpr")}:</span>
                      <span
                        className="text-54"
                        style={{ overflowWrap: "anywhere", paddingLeft: "5px" }}
                      >
                        {getAttributeValue(profile, 44)}
                      </span>
                    </Grid>
                  )}
                  {size(getAttributeValues(profile, 50)) > 0 && (
                    <Grid item className="flexCenter">
                      <span className="bold-500">{I18n.get("site_ocr")}:</span>
                      <span
                        className="text-54"
                        style={{ maxHeight: "16px", paddingLeft: "5px" }}
                      >
                        <EllipsedTextNew
                          text={get(getAttributeValues(profile, 50), [
                            0,
                            "displayValue",
                          ])}
                          maxWidth={228}
                        />
                      </span>
                      {size(getAttributeValues(profile, 50)) > 1 && (
                        <ClickAwayListener onClickAway={handleTooltipClose}>
                          <Tooltip
                            PopperProps={{
                              disablePortal: true,
                            }}
                            onClose={handleTooltipClose}
                            open={openTooltip}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            classes={{ tooltip: classes.tooltip }}
                            title={
                              <Grid container direction="column">
                                {map(
                                  filter(
                                    getAttributeValues(profile, 50),
                                    (v, i) => i > 0
                                  ),
                                  (v, i) => (
                                    <Grid item key={i}>
                                      {get(v, ["displayValue"])}
                                    </Grid>
                                  )
                                )}
                              </Grid>
                            }
                          >
                            <span
                              className={classes.ocrPlus}
                              onClick={handleTooltipOpen}
                            >{`+${size(getAttributeValues(profile, 50)) - 1
                              }`}</span>
                          </Tooltip>
                        </ClickAwayListener>
                      )}
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  } else if (handleClose && allow) {
    return (
      <Grid
        item
        xs
        style={{
          padding: "10px",
          borderRadius: "2px",
          backgroundColor: colors.lightGrey,
          ...props.style,
          border: "1px solid #dddddd"
        }}
        className={props.className}
        onClick={props.onClick}
      >
        <Grid container wrap="nowrap">
          <Grid
            item
            className={classes.pictureContainer}
            style={{ backgroundColor: !profileImage ? colors.mediumGrey : "" }}
          >
            {profileImage ? (
              <Picture
                url={profileImage}
                alt="profile_picture"
                maxHeight={73}
                style={{ maxWidth: "73px", maxHeight: "73px" }}
              />
            ) : (
              <img src={images.humanIconBlack} width="50px" alt="human" />
            )}
          </Grid>
          <Grid item xs className={classes.infoContainer} style={{ width: 220 }}>
            <Grid container direction="column">
              <Grid item>
                <Grid container>
                  <Grid item style={{ paddingRight: "5px" }}>
                    <img
                      src={getProfileTypeIcon(get(profile, ["type", "id"]))}
                      height={"12px"}
                      alt="profile_icon"
                    />
                  </Grid>
                  <Grid item xs className="bold-500 text-87">
                    {get(profile, ["type", "id"]) === 1 &&
                      get(profile, ["firstname"])
                      ? `${get(profile, ["firstname"])} ${get(profile, [
                        "lastname",
                      ])}`
                      : get(profile, ["name"]) || I18n.get("site_unknown")}
                    <img
                      src={images.closeIcon}
                      alt="close"
                      style={{ cursor: "pointer", float: "right" }}
                      onClick={() => handleClose("removeProfile", null, profile)}
                    />
                  </Grid>
                  {props.topRightNode && <Grid item>{props.topRightNode}</Grid>}
                </Grid>
              </Grid>
              <Grid
                item
                style={{
                  maxHeight: "20px",
                  overflow: "hidden",
                }}
              >
                <Grid container wrap="nowrap">
                  {associations && (
                    <Grid item xs={6}>
                      <Grid container wrap="nowrap">
                        <Grid item style={{ paddingRight: "5px" }}>
                          <img src={images.relationIcon} alt="relation" />
                        </Grid>
                        <Grid
                          item
                          className="text-54"
                          style={{ paddingRight: "9px", maxHeight: "20px" }}
                        >
                          <EllipsedTextNew
                            text={join(
                              map(associations, (a) =>
                                get(a, [`name_${language}`])
                              ),
                              ", "
                            )}
                            maxWidth={95}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <TagColor
                      color={
                        get(profile, ["tag", "color", "code"])
                          ? `#${profile.tag.color.code}`
                          : get(profile, ["tag", "color_rgb"])
                            ? `#${profile.tag.color_rgb}`
                            : "undefined"
                      }
                      name={
                        get(profile, ["tag", `name_${language}`]) ||
                        I18n.get("site_unknown")
                      }
                      maxWidth={100}
                      style={{ maxHeight: "16px" }}
                    />
                  </Grid>
                  {getAttributeValue(profile, 89) && (
                    <Grid item xs={6}>
                      <Grid container wrap="nowrap">
                        <Grid item style={{ paddingRight: "5px" }}>
                          <img src={images.carrierIcon} alt="carrier" />
                        </Grid>
                        <Grid
                          item
                          className="text-54"
                          style={{ paddingRight: "9px", maxHeight: "20px" }}
                        >
                          <EllipsedTextNew
                            text={getAttributeValue(profile, 89, lov, language)}
                            maxWidth={95}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              {get(profile, ["type", "id"]) === 1 && (
                <>
                  {getAttributeValue(profile, 34) && (
                    <Grid item className="flexCenter">
                      <img src={images.phoneIcon} alt="phone" />
                      <span
                        className="text-54"
                        style={{ overflowWrap: "anywhere", paddingLeft: "5px" }}
                      >
                        {formatPhone(getAttributeValue(profile, 34))}
                      </span>
                    </Grid>
                  )}
                  {getAttributeValue(profile, 33) && (
                    <Grid item className="flexCenter">
                      <img src={images.emailIcon} alt="email" />
                      <span
                        className="text-54"
                        style={{ maxHeight: "16px", paddingLeft: "5px" }}
                      >
                        <EllipsedTextNew
                          text={getAttributeValue(profile, 33)}
                          maxWidth={228}
                        />
                      </span>
                    </Grid>
                  )}
                </>
              )}
              {includes([2, 3], get(profile, ["type", "id"])) && (
                <>
                  {getAttributeValue(profile, 44) && (
                    <Grid item className="flexCenter">
                      <span className="bold-500">{I18n.get("site_lpr")}:</span>
                      <span
                        className="text-54"
                        style={{ overflowWrap: "anywhere", paddingLeft: "5px" }}
                      >
                        {getAttributeValue(profile, 44)}
                      </span>
                    </Grid>
                  )}
                  {size(getAttributeValues(profile, 50)) > 0 && (
                    <Grid item className="flexCenter">
                      <span className="bold-500">{I18n.get("site_ocr")}:</span>
                      <span
                        className="text-54"
                        style={{ maxHeight: "16px", paddingLeft: "5px" }}
                      >
                        <EllipsedTextNew
                          text={get(getAttributeValues(profile, 50), [
                            0,
                            "displayValue",
                          ])}
                          maxWidth={228}
                        />
                      </span>
                      {size(getAttributeValues(profile, 50)) > 1 && (
                        <ClickAwayListener onClickAway={handleTooltipClose}>
                          <Tooltip
                            PopperProps={{
                              disablePortal: true,
                            }}
                            onClose={handleTooltipClose}
                            open={openTooltip}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            classes={{ tooltip: classes.tooltip }}
                            title={
                              <Grid container direction="column">
                                {map(
                                  filter(
                                    getAttributeValues(profile, 50),
                                    (v, i) => i > 0
                                  ),
                                  (v, i) => (
                                    <Grid item key={i}>
                                      {get(v, ["displayValue"])}
                                    </Grid>
                                  )
                                )}
                              </Grid>
                            }
                          >
                            <span
                              className={classes.ocrPlus}
                              onClick={handleTooltipOpen}
                            >{`+${size(getAttributeValues(profile, 50)) - 1
                              }`}</span>
                          </Tooltip>
                        </ClickAwayListener>
                      )}
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  } else {
    return (
      <Grid
        item
        xs
        style={{
          padding: "10px",
          borderRadius: "2px",
          backgroundColor: colors.lightGrey,
          ...props.style,
          border: "1px solid #dddddd"
        }}
        className={props.className}
        onClick={props.onClick}
      >
        <Grid container wrap="nowrap">
          <Grid item xs className={classes.infoContainer} style={{ width: 220 }}>
            <Grid container direction="column">
              <Grid item>
                <Grid container>
                  <Grid item style={{ paddingRight: "5px" }}>
                    <img
                      src={getProfileTypeIcon(get(profile, ["type", "id"]))}
                      height={"12px"}
                      alt="profile_icon"
                    />
                  </Grid>
                  <Grid item xs className="bold-500 text-87">
                    {get(profile, ["type", "id"]) === 1 &&
                      get(profile, ["firstname"])
                      ? `${get(profile, ["firstname"])} ${get(profile, [
                        "lastname",
                      ])}`
                      : get(profile, ["name"]) || I18n.get("site_unknown")}
                    <img
                      src={images.closeIcon}
                      alt="close"
                      style={{ cursor: "pointer", float: "right" }}
                      onClick={() => handleClose("removeProfile", 1, profile)}
                    />
                  </Grid>
                  {props.topRightNode && <Grid item>{props.topRightNode}</Grid>}
                </Grid>
              </Grid>
              <Grid
                item
                style={{
                  maxHeight: "20px",
                  overflow: "hidden",
                }}
              >
                <Grid container wrap="nowrap">
                  {associations && (
                    <Grid item xs={6}>
                      <Grid container wrap="nowrap">
                        <Grid item style={{ paddingRight: "5px" }}>
                          <img src={images.relationIcon} alt="relation" />
                        </Grid>
                        <Grid
                          item
                          className="text-54"
                          style={{ paddingRight: "9px", maxHeight: "20px" }}
                        >
                          <EllipsedTextNew
                            text={join(
                              map(associations, (a) =>
                                get(a, [`name_${language}`])
                              ),
                              ", "
                            )}
                            maxWidth={95}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <TagColor
                      color={
                        get(profile, ["tag", "color", "code"])
                          ? `#${profile.tag.color.code}`
                          : get(profile, ["tag", "color_rgb"])
                            ? `#${profile.tag.color_rgb}`
                            : "undefined"
                      }
                      name={
                        get(profile, ["tag", `name_${language}`]) ||
                        I18n.get("site_unknown")
                      }
                      maxWidth={100}
                      style={{ maxHeight: "16px" }}
                    />
                  </Grid>
                  {getAttributeValue(profile, 89) && (
                    <Grid item xs={6}>
                      <Grid container wrap="nowrap">
                        <Grid item style={{ paddingRight: "5px" }}>
                          <img src={images.carrierIcon} alt="carrier" />
                        </Grid>
                        <Grid
                          item
                          className="text-54"
                          style={{ paddingRight: "9px", maxHeight: "20px" }}
                        >
                          <EllipsedTextNew
                            text={getAttributeValue(profile, 89, lov, language)}
                            maxWidth={95}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              {get(profile, ["type", "id"]) === 1 && (
                <>
                  {getAttributeValue(profile, 34) && (
                    <Grid item className="flexCenter">
                      <img src={images.phoneIcon} alt="phone" />
                      <span
                        className="text-54"
                        style={{ overflowWrap: "anywhere", paddingLeft: "5px" }}
                      >
                        {formatPhone(getAttributeValue(profile, 34))}
                      </span>
                    </Grid>
                  )}
                  {getAttributeValue(profile, 33) && (
                    <Grid item className="flexCenter">
                      <img src={images.emailIcon} alt="email" />
                      <span
                        className="text-54"
                        style={{ maxHeight: "16px", paddingLeft: "5px" }}
                      >
                        <EllipsedTextNew
                          text={getAttributeValue(profile, 33)}
                          maxWidth={228}
                        />
                      </span>
                    </Grid>
                  )}
                </>
              )}
              {includes([2, 3], get(profile, ["type", "id"])) && (
                <>
                  {getAttributeValue(profile, 44) && (
                    <Grid item className="flexCenter">
                      <span className="bold-500">{I18n.get("site_lpr")}:</span>
                      <span
                        className="text-54"
                        style={{ overflowWrap: "anywhere", paddingLeft: "5px" }}
                      >
                        {getAttributeValue(profile, 44)}
                      </span>
                    </Grid>
                  )}
                  {size(getAttributeValues(profile, 50)) > 0 && (
                    <Grid item className="flexCenter">
                      <span className="bold-500">{I18n.get("site_ocr")}:</span>
                      <span
                        className="text-54"
                        style={{ maxHeight: "16px", paddingLeft: "5px" }}
                      >
                        <EllipsedTextNew
                          text={get(getAttributeValues(profile, 50), [
                            0,
                            "displayValue",
                          ])}
                          maxWidth={228}
                        />
                      </span>
                      {size(getAttributeValues(profile, 50)) > 1 && (
                        <ClickAwayListener onClickAway={handleTooltipClose}>
                          <Tooltip
                            PopperProps={{
                              disablePortal: true,
                            }}
                            onClose={handleTooltipClose}
                            open={openTooltip}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            classes={{ tooltip: classes.tooltip }}
                            title={
                              <Grid container direction="column">
                                {map(
                                  filter(
                                    getAttributeValues(profile, 50),
                                    (v, i) => i > 0
                                  ),
                                  (v, i) => (
                                    <Grid item key={i}>
                                      {get(v, ["displayValue"])}
                                    </Grid>
                                  )
                                )}
                              </Grid>
                            }
                          >
                            <span
                              className={classes.ocrPlus}
                              onClick={handleTooltipOpen}
                            >{`+${size(getAttributeValues(profile, 50)) - 1
                              }`}</span>
                          </Tooltip>
                        </ClickAwayListener>
                      )}
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }


};

ProfileMatchExpress.propTypes = {
  profile: PropTypes.object,
  lov: PropTypes.object,
  searchValue: PropTypes.string,
};

const mapStateToProps = (state) => {
  return {
    language: state.user.language,
  };
};

export default connect(mapStateToProps)(
  withStyles(styles)(ProfileMatchExpress)
);
